import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { hasPermission } from '../config/permissions';
import { Box, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface ProtectedRouteProps {
  children: React.ReactNode;
  resource: string;
  action?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  resource, 
  action = 'view' 
}) => {
  const { user } = useAuthStore();
  const { t } = useTranslation();

  if (!user) {
    return <Navigate to="/auth/login" replace />;
  }

  const hasAccess = hasPermission(user.role, resource, action, user.customPermissions);

  // Debug logging
  console.log('ProtectedRoute check:', {
    resource,
    action,
    userRole: user.role,
    customPermissions: user.customPermissions,
    hasAccess
  });

  if (!hasAccess) {
    // Instead of redirecting, show an access denied message
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
        gap={2}
      >
        <Typography variant="h4" color="error">
          {t('errors.accessDenied', 'Access Denied')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('errors.noPermission', 'You do not have permission to access this page.')}
        </Typography>
        <Button
          variant="contained"
          onClick={() => window.history.back()}
          sx={{ mt: 2 }}
        >
          {t('common.goBack', 'Go Back')}
        </Button>
      </Box>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;